import { useState, useMemo } from "react";
import { Truck, MapPin, Loader2, CheckCircle2, AlertTriangle, Calculator } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { useConfig } from "./ConfiguracoesLayout";
import ConfigSection, { inputClass } from "./ConfigSection";
import { geocodeAddress } from "@/lib/geo-utils";
import { supabase } from "@/integrations/supabase/client";

const DeliveryConfig = () => {
  const { form, update, restaurant, toast } = useConfig();
  const [geocoding, setGeocoding] = useState(false);
  const [geoStatus, setGeoStatus] = useState<"idle" | "success" | "error">("idle");
  const [liveCoords, setLiveCoords] = useState<{ lat: number; lng: number } | null>(null);

  const radiusKm = parseFloat(form.delivery_radius_km) || 5;
  const useDynamicFee = parseFloat(form.delivery_fee_per_km) > 0;
  const freeUntilKm = parseFloat(form.delivery_free_until_km) || 0;
  const feePerKm = parseFloat(form.delivery_fee_per_km) || 0;

  // Use live coords (just geocoded) or saved coords from restaurant
  const mapCoords = useMemo(() => {
    if (liveCoords) return liveCoords;
    const lat = (restaurant as any)?.address_lat;
    const lng = (restaurant as any)?.address_lng;
    if (lat && lng) return { lat, lng };
    return null;
  }, [liveCoords, restaurant]);

  const handleGeocode = async () => {
    if (!restaurant) return;
    const streetWithNumber = [form.address_street, form.address_number].filter(Boolean).join(", ");
    const fullAddress = [streetWithNumber, form.address_neighborhood, form.address_city, form.address_state, "Brasil"].filter(Boolean).join(", ");

    if (!form.address_city) {
      toast({ title: "Preencha o endereço primeiro", description: "Vá em Endereço e preencha pelo menos a cidade.", variant: "destructive" });
      return;
    }

    setGeocoding(true);
    setGeoStatus("idle");
    try {
      const coords = await geocodeAddress(fullAddress, {
        street: streetWithNumber || undefined,
        city: form.address_city || undefined,
        state: form.address_state || undefined,
        postalcode: form.address_zip || undefined,
        country: "Brazil",
      });
      if (!coords) {
        setGeoStatus("error");
        toast({ title: "Endereço não encontrado", description: "Verifique se o endereço está completo e correto.", variant: "destructive" });
        return;
      }

      const { error } = await supabase.from("restaurants").update({
        address_lat: coords.lat,
        address_lng: coords.lng,
      } as any).eq("id", restaurant.id);

      if (error) throw error;
      setLiveCoords(coords);
      setGeoStatus("success");
      toast({ title: "Localização salva!", description: `Coordenadas: ${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}` });
    } catch (err: any) {
      setGeoStatus("error");
      toast({ title: "Erro ao geocodificar", description: err.message, variant: "destructive" });
    } finally {
      setGeocoding(false);
    }
  };

  return (
    <ConfigSection title="Delivery" icon={Truck}>
      <div className="space-y-6">
        {/* Basic settings */}
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Taxa fixa de entrega (R$)</Label>
            <Input type="number" step="0.01" value={form.delivery_fee} onChange={(e) => update("delivery_fee", e.target.value)} className={inputClass} />
            <p className="text-[10px] text-muted-foreground">Usada quando taxa dinâmica está desativada</p>
          </div>
          <div className="space-y-2">
            <Label>Pedido mínimo (R$)</Label>
            <Input type="number" step="0.01" value={form.min_order_value} onChange={(e) => update("min_order_value", e.target.value)} className={inputClass} />
          </div>
          <div className="space-y-2">
            <Label>Raio de entrega (km)</Label>
            <Input type="number" step="0.5" value={form.delivery_radius_km} onChange={(e) => update("delivery_radius_km", e.target.value)} className={inputClass} />
          </div>
        </div>

        {/* Dynamic fee config */}
        <div className="space-y-4 p-4 rounded-xl bg-secondary/50 border border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calculator className="h-4 w-4 text-primary" />
              <Label className="text-sm font-semibold">Taxa Dinâmica por Distância</Label>
            </div>
            <Switch
              checked={useDynamicFee}
              onCheckedChange={(checked) => {
                if (!checked) {
                  update("delivery_fee_per_km", "0");
                } else {
                  update("delivery_fee_per_km", "2");
                }
              }}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Quando ativada, a taxa de entrega é calculada automaticamente com base na distância do cliente.
          </p>

          {useDynamicFee && (
            <div className="grid grid-cols-2 gap-4 pt-2">
              <div className="space-y-2">
                <Label>Valor por km (R$)</Label>
                <Input
                  type="number"
                  step="0.50"
                  min="0"
                  value={form.delivery_fee_per_km}
                  onChange={(e) => update("delivery_fee_per_km", e.target.value)}
                  className={inputClass}
                />
              </div>
              <div className="space-y-2">
                <Label>Grátis até (km)</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  value={form.delivery_free_until_km}
                  onChange={(e) => update("delivery_free_until_km", e.target.value)}
                  className={inputClass}
                />
                <p className="text-[10px] text-muted-foreground">Entregas dentro desse raio são grátis</p>
              </div>
            </div>
          )}

          {useDynamicFee && (
            <div className="bg-muted/50 rounded-lg p-3 space-y-1">
              <p className="text-xs font-semibold text-foreground">Simulação de taxas:</p>
              {[1, 3, 5, 10, 15].filter(d => d <= radiusKm).map((d) => {
                const chargeableKm = Math.max(0, d - freeUntilKm);
                const fee = chargeableKm * feePerKm;
                return (
                  <div key={d} className="flex justify-between text-xs text-muted-foreground">
                    <span>{d} km</span>
                    <span className="font-medium text-foreground">
                      {fee === 0 ? "Grátis" : `R$ ${fee.toFixed(2).replace(".", ",")}`}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Visual radius slider */}
        <div className="space-y-3 p-4 rounded-xl bg-secondary/50 border border-border">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-semibold">Raio de Atendimento</Label>
            <span className="text-lg font-bold text-primary">{radiusKm} km</span>
          </div>
          <Slider
            value={[radiusKm]}
            onValueChange={([v]) => update("delivery_radius_km", v.toString())}
            min={1}
            max={50}
            step={0.5}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>1 km</span>
            <span>25 km</span>
            <span>50 km</span>
          </div>

          {/* Map or visual circle */}
          {mapCoords ? (
            <div className="space-y-2">
              <iframe
                title="Mapa do restaurante"
                width="100%"
                height="300"
                className="rounded-lg border border-border"
                style={{ filter: "saturate(0.85)" }}
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${mapCoords.lng - radiusKm * 0.012},${mapCoords.lat - radiusKm * 0.009},${mapCoords.lng + radiusKm * 0.012},${mapCoords.lat + radiusKm * 0.009}&layer=mapnik&marker=${mapCoords.lat},${mapCoords.lng}`}
              />
              <p className="text-xs text-muted-foreground text-center">
                📍 {mapCoords.lat.toFixed(5)}, {mapCoords.lng.toFixed(5)} — Raio: {radiusKm} km (~{(Math.PI * radiusKm * radiusKm).toFixed(0)} km²)
              </p>
            </div>
          ) : (
            <div className="flex items-center justify-center py-4">
              <div className="relative">
                <div
                  className="rounded-full border-2 border-dashed border-primary/40 bg-primary/5 flex items-center justify-center transition-all duration-300"
                  style={{
                    width: `${Math.min(200, 40 + radiusKm * 3.2)}px`,
                    height: `${Math.min(200, 40 + radiusKm * 3.2)}px`,
                  }}
                >
                  <div className="w-3 h-3 rounded-full bg-primary shadow-lg" />
                </div>
                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs text-muted-foreground">
                  Calcule a localização para ver o mapa
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Geocoding */}
        <div className="space-y-3 p-4 rounded-xl bg-secondary/50 border border-border">
          <div className="flex items-center gap-2 mb-1">
            <MapPin className="h-4 w-4 text-primary" />
            <Label className="text-sm font-semibold">Localização do Restaurante</Label>
          </div>
          <p className="text-xs text-muted-foreground">
            Clique para calcular as coordenadas do seu endereço. Isso permite validar automaticamente se o cliente está dentro da área de entrega.
          </p>
          <div className="flex items-center gap-3">
            <Button
              onClick={handleGeocode}
              disabled={geocoding}
              variant="outline"
              className="gap-2"
            >
              {geocoding ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
              {geocoding ? "Localizando..." : "Calcular Localização"}
            </Button>
            {geoStatus === "success" && (
              <span className="text-xs text-green-600 flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5" /> Localização salva
              </span>
            )}
            {geoStatus === "error" && (
              <span className="text-xs text-destructive flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" /> Não encontrado
              </span>
            )}
          </div>
        </div>
      </div>
    </ConfigSection>
  );
};

export default DeliveryConfig;
